import { isAfter, isBefore, isValid, parse, startOfDay } from 'date-fns';
import { DateFormatOptions } from 'shared/types/date-format-options';

import { dateInputSeparator } from './format-date-input';

export type ParseDateInputBounds = {
  minDate?: Date;
  maxDate?: Date;
};

// Day/month may be single-digit; the year stays exact-width so "22-07-89" isn't read as year 0089.
const toCompleteValue = (
  value: string,
  dateFormat: DateFormatOptions,
): string | null => {
  const partLengths = dateFormat
    .split(dateInputSeparator)
    .map((part) => part.length);
  const parts = value.split(dateInputSeparator);

  if (parts.length !== partLengths.length) {
    return null;
  }

  const isComplete = parts.every((part, index) => {
    if (partLengths[index] > 2) {
      return part.length === partLengths[index];
    }

    return !!part.length && part.length <= partLengths[index];
  });

  if (!isComplete) {
    return null;
  }

  return parts
    .map((part, index) => part.padStart(partLengths[index], '0'))
    .join(dateInputSeparator);
};

export const parseDateInput = (
  value: string,
  dateFormat: DateFormatOptions,
  referenceDate: Date,
  { minDate, maxDate }: ParseDateInputBounds = {},
): Date | null => {
  const completeValue = toCompleteValue(value, dateFormat);
  if (!completeValue) {
    return null;
  }

  const parsed = parse(completeValue, dateFormat, referenceDate);
  if (!isValid(parsed)) {
    return null;
  }

  const day = startOfDay(parsed);
  if (minDate && isBefore(day, startOfDay(minDate))) {
    return null;
  }

  if (maxDate && isAfter(day, startOfDay(maxDate))) {
    return null;
  }

  return parsed;
};
